import { useState } from "react";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import { useAppContextNonNull } from "../../../../contexts/AppContext";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import ISupplier_Delivery_Schedule_Day from "../../../../models/ISupplier_Delivery_Schedule_Day";
import DeliveryDeadlinesService from "../../../../services/DeliveryDeadlinesService";
import {
    DAY_LABEL_KEYS,
    IScheduleDayFormState,
    ScheduleDayErrors,
    defaultDayFormState,
    dayToFormState,
    formStateToDTO,
    validateScheduleDay,
} from "./deliveryDeadlineUtils";
import "./DeliveryScheduleDayForm.scss";

type Props = {
    scheduleGuid: string;
    existingDay?: ISupplier_Delivery_Schedule_Day;
    onSaved: () => void;
    onCancel: () => void;
};

const dayOptions = DAY_LABEL_KEYS.map((key, index) => ({ value: index, label: key }));

/**
 * Add/edit form for a single delivery day. Saving POSTs to the upsert endpoint; the day's
 * guid is sent when editing an existing day and omitted when adding, so the backend can add
 * a new day or update the right one (and reject a duplicate day-of-week) rather than
 * silently overwriting.
 */
export const DeliveryScheduleDayForm = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [form, setForm] = useState<IScheduleDayFormState>(() =>
        props.existingDay ? dayToFormState(props.existingDay) : defaultDayFormState()
    );
    const [errors, setErrors] = useState<ScheduleDayErrors>({});
    const [isSaving, setIsSaving] = useState(false);

    const setDay = (field: keyof IScheduleDayFormState) => (e: any) => {
        setForm((prev) => ({ ...prev, [field]: parseInt(e.target.value, 10) }));
    };

    const setTime = (field: keyof IScheduleDayFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const toggleReminder = (e: any) => {
        setForm((prev) => ({ ...prev, send_reminder: e.target.value }));
    };

    const setMinimum = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, minimum_order_total: e.target.value }));
    };

    const save = async () => {
        const validationErrors = validateScheduleDay(form);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        setIsSaving(true);
        try {
            const dto = { ...formStateToDTO(form), guid: props.existingDay?.guid };
            await DeliveryDeadlinesService.upsertDeliveryScheduleDay(props.scheduleGuid, dto, userModel, supplierModel);
            uiContext.showNotification({ message: "delivery_day_saved", level: NotificationLevelEnum.success });
            props.onSaved();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="DeliveryScheduleDayForm rounded p-3 mt-2">
            <div className="row g-3">
                <div className="col-md-4">
                    <OwLabel title="delivery_day" noBottomMargin={true} />
                    <OwSelectOption
                        name="delivery_day_of_week"
                        options={dayOptions}
                        valueProp="value"
                        titleProp="label"
                        translateTitle={true}
                        showBlankFirst={false}
                        selectedOption={{ value: form.delivery_day_of_week }}
                        selectedOptionProp="value"
                        fn={setDay("delivery_day_of_week")}
                    />
                </div>
            </div>

            <hr />

            <OwLabel title="hard_deadline" className="fw-bold" />
            <div className="row g-3">
                <div className="col-md-4">
                    <OwLabel title="day" noBottomMargin={true} />
                    <OwSelectOption
                        name="hard_deadline_day_of_week"
                        options={dayOptions}
                        valueProp="value"
                        titleProp="label"
                        translateTitle={true}
                        showBlankFirst={false}
                        selectedOption={{ value: form.hard_deadline_day_of_week }}
                        selectedOptionProp="value"
                        fn={setDay("hard_deadline_day_of_week")}
                    />
                </div>
                <div className="col-md-4">
                    <OwLabel title="time" noBottomMargin={true} />
                    <input
                        type="time"
                        aria-label="hard deadline time"
                        className="form-control form-control-sm"
                        value={form.hard_deadline_time}
                        onChange={setTime("hard_deadline_time")}
                    />
                    {errors.hard_deadline_time && <OwLabel title={errors.hard_deadline_time} className="text-danger small" />}
                </div>
            </div>

            <hr />

            <div className="mb-2">
                <OwToggle fn={toggleReminder} name="send_reminder" label="send_a_reminder" active={form.send_reminder} labelOnRight={true} />
            </div>

            {form.send_reminder && (
                <div className="row g-3">
                    <div className="col-md-4">
                        <OwLabel title="day" noBottomMargin={true} />
                        <OwSelectOption
                            name="soft_reminder_day_of_week"
                            options={dayOptions}
                            valueProp="value"
                            titleProp="label"
                            translateTitle={true}
                            showBlankFirst={false}
                            selectedOption={{ value: form.soft_reminder_day_of_week }}
                            selectedOptionProp="value"
                            fn={setDay("soft_reminder_day_of_week")}
                        />
                    </div>
                    <div className="col-md-4">
                        <OwLabel title="time" noBottomMargin={true} />
                        <input
                            type="time"
                            aria-label="soft reminder time"
                            className="form-control form-control-sm"
                            step={3600}
                            value={form.soft_reminder_time}
                            onChange={setTime("soft_reminder_time")}
                        />
                        {errors.soft_reminder_time && <OwLabel title={errors.soft_reminder_time} className="text-danger small" />}
                        {errors.soft_reminder && <OwLabel title={errors.soft_reminder} className="text-danger small" />}
                        <OwLabel title="reminder_minutes_helper" className="text-muted small" />
                    </div>
                </div>
            )}

            <hr />

            <div className="row g-3">
                <div className="col-md-4">
                    <OwLabel title="minimum_order_total" noBottomMargin={true} />
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        aria-label="minimum order total"
                        className="form-control form-control-sm"
                        value={form.minimum_order_total}
                        onChange={setMinimum}
                        placeholder="0.00"
                    />
                    {errors.minimum_order_total && <OwLabel title={errors.minimum_order_total} className="text-danger small" />}
                    <OwLabel title="minimum_order_total_helper" className="text-muted small" />
                </div>
            </div>

            <div className="mt-3 d-flex gap-2">
                <OwButton title="save" fn={save} className="btn-primary" disabled={isSaving} />
                <OwButton title="cancel" fn={props.onCancel} className="btn-secondary" disabled={isSaving} />
            </div>
        </div>
    );
};

export default DeliveryScheduleDayForm;
